import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import type { DistribucionMode } from '../../types';

interface RetirarCopiasStep1Props {
  copiasDisponibles: number;
  cantidadSeleccionada: number;
  onCantidadChange: (cantidad: number) => void;
  distribucionMode: DistribucionMode;
  onModeChange: (mode: DistribucionMode) => void;
  error?: string;
}

export const RetirarCopiasStep1: React.FC<RetirarCopiasStep1Props> = ({
  copiasDisponibles,
  cantidadSeleccionada,
  onCantidadChange,
  distribucionMode,
  onModeChange,
  error,
}) => {
  return (
    <div className="space-y-6">
      {/* Info de copias disponibles */}
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
        <p className="text-sm text-blue-900">
          <span className="font-semibold">Copias disponibles:</span> {copiasDisponibles}
        </p>
      </div>

      {/* Selección de cantidad */}
      <div className="space-y-2">
        <Label htmlFor="cantidad-copias">
          ¿Cuántas copias desea retirar?
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Input
          id="cantidad-copias"
          type="number"
          min={1}
          max={copiasDisponibles}
          value={cantidadSeleccionada || ''}
          onChange={(e) => {
            const value = Number.parseInt(e.target.value, 10);
            if (!Number.isNaN(value) && value >= 1 && value <= copiasDisponibles) {
              onCantidadChange(value);
            } else if (e.target.value === '') {
              onCantidadChange(0);
            }
          }}
          placeholder={`Ingrese un número entre 1 y ${copiasDisponibles}`}
          className={error ? 'border-red-300' : ''}
        />
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </div>

      {/* Modo de distribución */}
      {cantidadSeleccionada > 0 && (
        <div className="space-y-3">
          <Label>
            ¿Cómo desea distribuir las copias?
            <span className="text-red-500 ml-1">*</span>
          </Label>

          <div className="space-y-3">
            <div className="flex items-start space-x-3 rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                id="mode-single"
                name="distribution-mode"
                value="single"
                checked={distribucionMode === 'single'}
                onChange={(e) => onModeChange(e.target.value as DistribucionMode)}
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="mode-single" className="cursor-pointer font-medium">
                  Una sola persona retira todas
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Una persona retirará {cantidadSeleccionada} {cantidadSeleccionada === 1 ? 'copia' : 'copias'}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                id="mode-multiple"
                name="distribution-mode"
                value="multiple"
                checked={distribucionMode === 'multiple'}
                onChange={(e) => onModeChange(e.target.value as DistribucionMode)}
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="mode-multiple" className="cursor-pointer font-medium">
                  Distribuir entre varias personas
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Varias personas retirarán las {cantidadSeleccionada} {cantidadSeleccionada === 1 ? 'copia' : 'copias'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
